import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Tabs, 
  Table, 
  Input, 
  Button, 
  Space, 
  message,
  Row,
  Col,
  Checkbox
} from 'antd';
import { 
  SearchOutlined, 
  ReloadOutlined,
  SettingOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as roleService from '../../services/role/roleService';
import * as accountService from '../../services/account/accountService';
import EllipsisCell from '@/components/common/EllipsisCell';

const { TabPane } = Tabs;

/**
 * Admin Store Authorization Page Component
 * @description Modal page for managing store authorization for admin accounts
 */
const AdminStoreAuthPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const loginId = searchParams.get('loginId');
  
  const [visible, setVisible] = useState(true);
  const [activeTab, setActiveTab] = useState('unauthorized');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Search filters
  const [storeNameFilter, setStoreNameFilter] = useState('');
  const [storeCodeFilter, setStoreCodeFilter] = useState('');
  
  // Data
  const [authorizedStores, setAuthorizedStores] = useState([]);
  const [unauthorizedStores, setUnauthorizedStores] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  
  // Pagination
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // Mock data for all stores
  const allMockStores = [
    {
      code: 'STORE560',
      name: 'Store560 Data Permission',
      storePermission: 'Store560 Data Permission'
    },
    {
      code: 'STORE542',
      name: 'Store542 Data Permission',
      storePermission: 'Store542 Data Permission'
    },
    {
      code: 'STORE548',
      name: 'Store548 Data Permission',
      storePermission: 'Store548 Data Permission'
    },
    {
      code: 'STORE547',
      name: 'Store547 Data Permission',
      storePermission: 'Store547 Data Permission'
    },
    {
      code: 'STORE563',
      name: 'Store563 Data Permission',
      storePermission: 'Store563 Data Permission'
    }
  ];

  // Track which store codes are authorized (initially none)
  const [authorizedCodes, setAuthorizedCodes] = useState([]);

  // Initialize: load all stores into unauthorized on mount
  useEffect(() => {
    setLoading(true);
    // All stores start as unauthorized
    setUnauthorizedStores(allMockStores);
    setAuthorizedStores([]);
    setLoading(false);
  }, []);

  // Recompute displayed lists whenever authorizedCodes or filters change
  useEffect(() => {
    const authStores = allMockStores.filter(s => authorizedCodes.includes(s.code));
    const unauthStores = allMockStores.filter(s => !authorizedCodes.includes(s.code));

    // Apply search filters
    const applyFilters = (list) => {
      let filtered = [...list];
      if (storeNameFilter) {
        filtered = filtered.filter(store =>
          store.name.toLowerCase().includes(storeNameFilter.toLowerCase())
        );
      }
      if (storeCodeFilter) {
        filtered = filtered.filter(store =>
          store.code.toLowerCase().includes(storeCodeFilter.toLowerCase())
        );
      }
      return filtered;
    };

    setAuthorizedStores(applyFilters(authStores));
    setUnauthorizedStores(applyFilters(unauthStores));
  }, [authorizedCodes, storeNameFilter, storeCodeFilter]);

  // Handle query - filters are already reactive via useEffect, just reset pagination
  const handleQuery = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Handle reset
  const handleReset = () => {
    setStoreNameFilter('');
    setStoreCodeFilter('');
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Handle add authorize - move selected stores from unauthorized to authorized
  const handleAddAuthorize = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('adminStoreAuth.selectAtLeastOne'));
      return;
    }

    setAuthorizedCodes(prev => [...prev, ...selectedRowKeys]);
    setSelectedRowKeys([]);
    message.success(t('adminStoreAuth.authorizeSuccess', { count: selectedRowKeys.length }));
  };

  // Handle remove authorize - move selected stores from authorized to unauthorized
  const handleRemoveAuthorize = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('adminStoreAuth.selectAtLeastOne'));
      return;
    }

    setAuthorizedCodes(prev => prev.filter(code => !selectedRowKeys.includes(code)));
    setSelectedRowKeys([]);
    message.success(t('adminStoreAuth.removeSuccess', { count: selectedRowKeys.length }));
  };

  // Handle tab change
  const handleTabChange = (key) => {
    setActiveTab(key);
    setSelectedRowKeys([]);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Handle table change
  const handleTableChange = (newPagination) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total
    });
  };

  // Handle close
  const handleClose = () => {
    setVisible(false);
    navigate('/account-management');
  };

  // Table columns
  const columns = [
    {
      title: t('adminStoreAuth.storeName'),
      dataIndex: 'name',
      key: 'name',
      width: '30%',
      render: (text) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('adminStoreAuth.storeCode'),
      dataIndex: 'code',
      key: 'code',
      width: '30%',
      render: (text) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('adminStoreAuth.storePermission'),
      dataIndex: 'storePermission',
      key: 'storePermission',
      width: '40%',
      render: (text) => <EllipsisCell>{text || '-'}</EllipsisCell>,
    }
  ];

  // Row selection
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys);
    }
  };

  const currentData = activeTab === 'authorized' ? authorizedStores : unauthorizedStores;

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <SettingOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          {t('adminStoreAuth.authorize')}
        </div>
      }
      open={visible}
      onCancel={handleClose}
      width={1200}
      footer={null}
      closeIcon={<CloseOutlined />}
      styles={{
        body: { padding: '0' }
      }}
    >
      <Tabs 
        activeKey={activeTab} 
        onChange={handleTabChange}
        style={{ marginBottom: 0 }}
        tabBarStyle={{ 
          paddingLeft: 24, 
          paddingRight: 24,
          marginBottom: 0 
        }}
      >
        <TabPane tab={t('adminStoreAuth.authorizedStores')} key="authorized">
          <div style={{ padding: '16px 24px' }}>
            {/* Search Area */}
            <div style={{ 
              background: '#f5f5f5', 
              padding: '16px', 
              borderRadius: '4px',
              marginBottom: 16 
            }}>
              <Row gutter={16}>
                <Col span={8}>
                  <div style={{ marginBottom: 8 }}>
                    <span>{t('adminStoreAuth.storeName')}</span>
                  </div>
                  <Input 
                    placeholder={t('adminStoreAuth.pleaseEnter')}
                    value={storeNameFilter}
                    onChange={(e) => setStoreNameFilter(e.target.value)}
                    onPressEnter={handleQuery}
                  />
                </Col>
                <Col span={8}>
                  <div style={{ marginBottom: 8 }}>
                    <span>{t('adminStoreAuth.storeCode')}</span>
                  </div>
                  <Input 
                    placeholder={t('adminStoreAuth.pleaseEnter')}
                    value={storeCodeFilter}
                    onChange={(e) => setStoreCodeFilter(e.target.value)}
                    onPressEnter={handleQuery}
                  />
                </Col>
                <Col span={8} style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <Space>
                    <Button 
                      type="primary" 
                      icon={<SearchOutlined />}
                      onClick={handleQuery}
                    >
                      {t('adminStoreAuth.query')}
                    </Button>
                    <Button 
                      icon={<ReloadOutlined />}
                      onClick={handleReset}
                    >
                      {t('adminStoreAuth.reset')}
                    </Button>
                    <Button 
                      icon={<SettingOutlined />}
                    />
                  </Space>
                </Col>
              </Row>
              
              {/* Hide/Show toggle */}
              <div style={{ 
                textAlign: 'center', 
                marginTop: 8,
                cursor: 'pointer',
                color: '#1890ff'
              }}>
                <span>▲ {t('adminStoreAuth.hide')}</span>
              </div>
            </div>

            {/* Selection Info */}
            <div style={{ marginBottom: 16 }}>
              <Space>
                <span>{t('adminStoreAuth.selectInfo', { selected: selectedRowKeys.length, total: pagination.total })}</span>
                <Button 
                  type="primary"
                  onClick={handleRemoveAuthorize}
                  disabled={selectedRowKeys.length === 0}
                  loading={saving}
                >
                  {t('adminStoreAuth.removeAuthorize')}
                </Button>
              </Space>
            </div>

            {/* Table */}
            <Table
              rowSelection={rowSelection}
              columns={columns}
              dataSource={currentData}
              rowKey="code"
              loading={loading}
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => t('adminStoreAuth.totalItems', { total })
              }}
              onChange={handleTableChange}
              scroll={{ y: 400 }}
            />

            {/* Footer */}
            <div style={{ 
              textAlign: 'right', 
              marginTop: 16,
              paddingTop: 16,
              borderTop: '1px solid #f0f0f0'
            }}>
              <Button 
                type="primary" 
                onClick={handleClose}
              >
                {t('adminStoreAuth.back')}
              </Button>
            </div>
          </div>
        </TabPane>

        <TabPane tab={t('adminStoreAuth.unauthorizedStores')} key="unauthorized">
          <div style={{ padding: '16px 24px' }}>
            {/* Search Area */}
            <div style={{ 
              background: '#f5f5f5', 
              padding: '16px', 
              borderRadius: '4px',
              marginBottom: 16 
            }}>
              <Row gutter={16}>
                <Col span={8}>
                  <div style={{ marginBottom: 8 }}>
                    <span>{t('adminStoreAuth.storeName')}</span>
                  </div>
                  <Input 
                    placeholder={t('adminStoreAuth.pleaseEnter')}
                    value={storeNameFilter}
                    onChange={(e) => setStoreNameFilter(e.target.value)}
                    onPressEnter={handleQuery}
                  />
                </Col>
                <Col span={8}>
                  <div style={{ marginBottom: 8 }}>
                    <span>{t('adminStoreAuth.storeCode')}</span>
                  </div>
                  <Input 
                    placeholder={t('adminStoreAuth.pleaseEnter')}
                    value={storeCodeFilter}
                    onChange={(e) => setStoreCodeFilter(e.target.value)}
                    onPressEnter={handleQuery}
                  />
                </Col>
                <Col span={8} style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <Space>
                    <Button 
                      type="primary" 
                      icon={<SearchOutlined />}
                      onClick={handleQuery}
                    >
                      {t('adminStoreAuth.query')}
                    </Button>
                    <Button 
                      icon={<ReloadOutlined />}
                      onClick={handleReset}
                    >
                      {t('adminStoreAuth.reset')}
                    </Button>
                    <Button 
                      icon={<SettingOutlined />}
                    />
                  </Space>
                </Col>
              </Row>
              
              {/* Hide/Show toggle */}
              <div style={{ 
                textAlign: 'center', 
                marginTop: 8,
                cursor: 'pointer',
                color: '#1890ff'
              }}>
                <span>▲ {t('adminStoreAuth.hide')}</span>
              </div>
            </div>

            {/* Selection Info */}
            <div style={{ marginBottom: 16 }}>
              <Space>
                <span>{t('adminStoreAuth.selectInfo', { selected: selectedRowKeys.length, total: pagination.total })}</span>
                <Button 
                  type="primary"
                  onClick={handleAddAuthorize}
                  disabled={selectedRowKeys.length === 0}
                  loading={saving}
                >
                  {t('adminStoreAuth.addAuthorize')}
                </Button>
              </Space>
            </div>

            {/* Table */}
            <Table
              rowSelection={rowSelection}
              columns={columns}
              dataSource={currentData}
              rowKey="code"
              loading={loading}
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => t('adminStoreAuth.totalItems', { total })
              }}
              onChange={handleTableChange}
              scroll={{ y: 400 }}
            />

            {/* Footer */}
            <div style={{ 
              textAlign: 'right', 
              marginTop: 16,
              paddingTop: 16,
              borderTop: '1px solid #f0f0f0'
            }}>
              <Button 
                type="primary" 
                onClick={handleClose}
              >
                {t('adminStoreAuth.back')}
              </Button>
            </div>
          </div>
        </TabPane>
      </Tabs>
    </Modal>
  );
};

export default AdminStoreAuthPage;
